import React from 'react';
import { List, Radio, SingleRange, DualRange } from 'src/components/filters';
import { useAppContext } from 'src/components/app-context';
import { FilterWrapper, FilterInput } from './styles';
import { filterChangeAction } from '../../../../actions/filter';
import { useDispatch } from 'react-redux';
import { TextButton } from '@prisjakt/ui/button/variants';

type Property = {
  item_type: string; // eslint-disable-line camelcase
  id: string;
  name: string;
  unit: string | null;
  description: string | null;
  children?: Property[];
  has_icon?: boolean; // eslint-disable-line camelcase
  type?: string;
};

interface FilterProps {
  categoryId: string;
  layout: 'vertical' | 'horizontal';
  displayFilter: Property;
  query: Record<string, any>;
  filteredValues: Record<string, any>;
  onToggleAllValues: (id: string) => void;
  limitValues?: number;
  showAllValues: boolean;
}

export const Filter: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<FilterProps>>
> = props => {
  const [expanded, setExpanded] = React.useState<string[]>([]);
  const [inputValue, setInputValue] = React.useState<string>(
    props.query ? props.query[props.displayFilter.id] : ''
  );
  const dispatch = useDispatch();

  const {
    displayFilter,
    filteredValues,
    query,
    layout,
    showAllValues,
    categoryId,
    onToggleAllValues,
  } = props;

  function handleInputKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    const target = event.target as HTMLInputElement;

    if (event.key === 'Enter') {
      dispatch(
        filterChangeAction(categoryId, {
          id: displayFilter.id,
          value: target.value,
          children: undefined,
          section: {
            name: displayFilter.name,
            unit: displayFilter.unit,
          },
          parents: [],
          filterType: 'string',
        })
      );

      target.blur();
    }
  }

  function handleRadioOnChange(selected: { id: string }) {
    dispatch(
      filterChangeAction(categoryId, {
        id: displayFilter.id,
        section: { name: displayFilter.name, unit: displayFilter.unit },
        value: selected.id,
        filterType: 'boolean',
      })
    );
  }

  function handleCheckboxOnChange(
    filterType: 'list' | 'slider',
    selected: any
  ) {
    dispatch(
      filterChangeAction(categoryId, {
        ...selected,
        id: displayFilter.id,
        section: {
          name: displayFilter.name,
          unit: displayFilter.unit,
        },
        filterType,
      })
    );
  }

  function handleOnExpand(id: string, event: Event) {
    event.preventDefault();
    setExpanded(prevState =>
      prevState.includes(id)
        ? prevState.filter(item => item !== id)
        : prevState.concat([id])
    );
  }

  const { t } = useAppContext();

  if (!displayFilter.type) return null;

  switch (displayFilter.type) {
    case 'boolean-reduced':
    case 'boolean': {
      const filterVal =
        filteredValues[displayFilter.id] &&
        filteredValues[displayFilter.id].values
          ? filteredValues[displayFilter.id].values
          : {};
      return (
        <Radio
          id={displayFilter.id}
          key={displayFilter.id}
          selected={query[displayFilter.id]}
          layout={layout}
          values={[
            {
              id: true,
              text: t('filter', 'properties.select-true'),
              products: filterVal.true,
            },
            {
              id: false,
              text: t('filter', 'properties.select-false'),
              products: filterVal.false,
            },
          ]}
          onChange={(selected: { id: string; checked: boolean }) =>
            handleRadioOnChange(selected)
          }
        />
      );
    }
    case 'list-or':
    case 'list-and':
    case 'list-or-reduced':
    case 'list-and-reduced':
    case 'list-tree-and':
    case 'list-tree-or': {
      if (!filteredValues[displayFilter.id].values) {
        return null;
      }

      const showExpansion = filteredValues[displayFilter.id]
        ? filteredValues[displayFilter.id].values.length > 5
        : false;

      const isLimited =
        showExpansion && showAllValues === false && props.limitValues;
      const limitedValues = isLimited
        ? filteredValues[displayFilter.id].values.slice(0, props.limitValues)
        : filteredValues[displayFilter.id].values;

      return (
        <FilterWrapper
          key={`${displayFilter.id}_wrapper`}
          horizontal={layout === 'horizontal'}
        >
          <List
            id={displayFilter.id}
            key={displayFilter.id}
            values={limitedValues}
            selected={query[displayFilter.id]}
            autoSelectChildren={false}
            onExpand={(event: Event, id: string) => handleOnExpand(id, event)}
            expanded={expanded}
            layout={layout}
            onChange={(selected: any) =>
              handleCheckboxOnChange('list', selected)
            }
          />

          {showExpansion && layout === 'vertical' && (
            <TextButton
              onClick={() => onToggleAllValues(displayFilter.id)}
              data-test="MoreFiltersToggleButton"
            >
              {t(
                'filter',
                isLimited
                  ? 'properties.quantity-show-more'
                  : 'properties.quantity-show-less'
              )}
            </TextButton>
          )}
        </FilterWrapper>
      );
    }
    case 'date-year':
    case 'integer':
    case 'decimal': {
      return (
        <SingleRange
          // id={displayFilter.id}
          key={displayFilter.id}
          type={displayFilter.type}
          values={filteredValues[displayFilter.id]}
          selected={query[displayFilter.id]}
          layout={layout}
          onChange={(selected: any) =>
            handleCheckboxOnChange('slider', selected)
          }
        />
      );
    }
    case 'interval-decimal-touch':
    case 'interval-decimal-contain':
    case 'interval-decimal-span':
    case 'interval-integer-touch':
    case 'interval-integer-contain':
    case 'interval-integer-span': {
      return (
        <DualRange
          id={displayFilter.id}
          key={displayFilter.id}
          type={displayFilter.type}
          values={filteredValues[displayFilter.id]}
          selected={query[displayFilter.id]}
          layout={layout}
          onChange={(selected: any) =>
            handleCheckboxOnChange('slider', selected)
          }
        />
      );
    }
    case 'string': {
      return (
        <FilterInput
          key={displayFilter.id}
          placeholder={displayFilter.name}
          value={inputValue}
          onKeyPress={event => handleInputKeyPress(event)}
          onChange={event => {
            setInputValue(event.target.value);
          }}
        />
      );
    }
    default: {
      return (
        <div>
          {displayFilter.name} with
          {displayFilter.type}
        </div>
      );
    }
  }
};
